import { Add, ArrowLeft, ArrowRight, ContentCopy, Delete, Edit } from '@mui/icons-material';
import { Button, FormControl, FormControlLabel, FormGroup, IconButton, InputLabel, MenuItem, Select, Switch, TextField, Tooltip } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import styles from './Refine.module.css';
import buildingConfStyles from '../BuildingConf.module.css';
import { alignment } from '../models/alignment.enum';
import { Part } from '../models/part';
import { Column } from '../models/column';
import { ColumnDefinition, inclusionEnum } from '../models/ColumnDefinition/column-definition';
import { Segment } from '../models/segment';
import { RefineService } from './refine.service';
import { ColDefOption } from '../../../app-confi/services/building-helper.service';
import { ServiceManager } from '../../../services/service-manager';
import { SectionJSON } from '../../../models/section-json';
import { usePrevious } from '../../../common/react-helpers';
import { segmentType as corridorSegmentType } from '../Corridor/models/segment-type';
import { segmentType as hybridSegmentType } from '../Hybrid/models/segment-type';
import { segmentType as rowhouseSegmentType } from '../Rowhouse/models/segment-type';
import { segmentType as towerSegmentType } from '../Rowhouse/models/segment-type';
import { HybridSegmentSelectorProps } from '../Hybrid/HybridSegmentSelector/HybridSegmentSelector';
import FlatOptions from '../../Common/Inputs/FlatOptions/FlatOptions';
import ToggleOptions, { ToggleOption } from '../../Common/Inputs/ToggleOptions/ToggleOptions';
import { buildingType } from '../models/building-type.enum';

const jointSegmentType = { ...corridorSegmentType, ...towerSegmentType, ...hybridSegmentType, ...rowhouseSegmentType };

const Refine = ({
    part,
    bt,
    buildingId,
    segmentTypesMultipleFloors,
    segmentTypesAllowCustom,
    columnDefs,
    updateBuildingColumn,
    updateBuildingColumns,
    getDefaultColumnTypeForPart,
    setUpDefaultColumnFromDefinition,
    floorHeight,
    sectionJson,
    SegmentComponent,
    getVariantName,
    getSegmentName,
    getSegmentSubtypeName,
}: {
    part: Part<any, any>,
    bt: buildingType,
    buildingId: string,
    segmentTypesMultipleFloors: string[],
    segmentTypesAllowCustom: string[],
    columnDefs: ColumnDefinition<any, any, any, any, any>[],
    updateBuildingColumn,
    updateBuildingColumns,
    getDefaultColumnTypeForPart,
    setUpDefaultColumnFromDefinition,
    floorHeight: number,
    sectionJson: SectionJSON,
    SegmentComponent?: FC<HybridSegmentSelectorProps>,
    getVariantName: <VariantEnum>(variant: VariantEnum) => string,
    getSegmentName: <SegmentTypeEnum>(segment: SegmentTypeEnum) => string,
    getSegmentSubtypeName: <SegmentSubtypeEnum>(segment: SegmentSubtypeEnum) => string,
}) => {

    const getColumnsAsOptions = (): ColDefOption<string>[] => {
        let res: ColDefOption<string>[];
        if (part.columns) {
            const columnDefinitions = part.columns.map(c => ({ ...columnDefs.find(cd => cd.type === c.type), id: c.id }));
            res = columnDefinitions.map((colDef) => {
                const col = ServiceManager.buildingHelperService.getColumnOptions(colDef, 'middle', sectionJson);
                col.id = colDef.id;
                return col;
            })
        }
        return res;
    }

    const getColumnsFiltered = (): ColDefOption<string>[] => {
        let res: ColDefOption<string>[];
        let filteredDefs = columnDefs;

        if (partIsBody()) {
            filteredDefs = columnDefs.filter(cd => cd.columnSubtypes.includes('moosplaza') || cd.columnSubtypes.includes('body'));
        }
        else {
            filteredDefs = columnDefs.filter(cd => cd.columnSubtypes.includes('head'));
        }
        const moosplaza = filterOptions.find(o => o.title === 'Entree');
        if (moosplaza && moosplaza.value) {
            filteredDefs = filteredDefs.filter(cd => cd.columnSubtypes.includes('moosplaza'));
        }
        const elevator = filterOptions.find(o => o.title === 'Lift');
        if (elevator && elevator.value) {
            filteredDefs = filteredDefs.filter(cd => cd.inclusions.includes(inclusionEnum.elevator));
        }
        const staircase = filterOptions.find(o => o.title === 'Trappenhuis');
        if (staircase && staircase.value) {
            filteredDefs = filteredDefs.filter(cd => cd.inclusions.includes(inclusionEnum.staircase));
        }
        res = filteredDefs.map((colDef) => {
            const col = ServiceManager.buildingHelperService.getColumnOptions(colDef, 'middle', sectionJson);
            return col;
        })
        return res;
    }
    const previousPart = usePrevious<Part<any, any>>(part);
    const [columnOptions, setColumnOptions] = useState<ColDefOption<string>[]>(
        getColumnsAsOptions()
    );

    const partIsBody = () => {
        const partType = part.type;
        return (partType === 'body' || partType === 'bodyA' || partType === 'bodyB')
    }

    const getFilterOptions = (): ToggleOption[] => {
        const options: ToggleOption[] = [];
        if (bt !== buildingType.rowhouse) {
            if (partIsBody()) {
                options.push({
                    title: 'Entree',
                    value: false,
                });
            }
            options.push({
                title: 'Lift',
                value: false,
            });
            options.push({
                title: 'Trappenhuis',
                value: false,
            });
        }
        return options;
    }

    const [filterOptions, setFilterOptions] = useState<ToggleOption[]>(
        getFilterOptions()
    );

    const [chosenColType, setChosenColType] = useState<string>('');

    const [filteredColumnOptions, setFilteredColumnOptions] = useState<ColDefOption<string>[]>(
        getColumnsFiltered()
    );

    const [selectedColumnId, setSelectedColumnId] = useState<string>(
        part.columns.length ? part.columns[0].id : ''
    );

    const [activeSegment, setActiveSegment] = useState<'plinth' | 'middle' | 'crown'>(
        jointSegmentType.middle
    );

    const [editingColumn, setEditingColumn] = useState<boolean>(
        true
    );

    const [changingColumnType, setChangingColumnType] = useState<boolean>(
        false
    );

    const [addingColumn, setAddingColumn] = useState<boolean>(
        false
    );

    const [highlightingDisabled, setHighlightingDisabled] = useState<boolean>(
        false
    );

    useEffect(() => {
        const options = getColumnsFiltered();
        setFilteredColumnOptions(options);
    }, [filterOptions])

    const updateOptions = (currentId: string) => {
        const options = getColumnsAsOptions();
        setColumnOptions(options);
        setSelectedColumnId(currentId);
        setTimeout(() => {
            ServiceManager.sceneManager.modelLoaderService.highlightGroupsSugroup(buildingId, currentId, 'select');
        })
    }

    useEffect(() => {
        if (part && part.columns) {
            if (previousPart && part.type !== previousPart.type) {
                const fo = getFilterOptions();
                setFilterOptions(fo)
            }
            const id = previousPart && previousPart.type !== part.type && part.columns.length ? part.columns[0].id : selectedColumnId;
            if (part.columns.some(c => c.id === id)) {
                updateOptions(id);
            } else if (part.columns.length) {
                updateOptions(part.columns[0].id);
            }
        }
    }, [part])

    const getFilteredColumnOptions = (editing: boolean) => {
        return filteredColumnOptions && filteredColumnOptions.length ?
            <div className={buildingConfStyles.formControl}>
                <FormControl size="medium" fullWidth >
                    <InputLabel id={"column-filtered-selection-label"}>Kolom Type</InputLabel>
                    <Select
                        labelId={"column-filtered-selection-label"}
                        id={"column-filtered-selection-id"}
                        data-testid="column-filtered-selection-id"
                        value={chosenColType}
                        onChange={(event) => { setChosenColType(event.target.value) }}
                        label="Kolom Type"
                    >
                        {
                            filteredColumnOptions.map((bs, index) => {
                                return (
                                    <MenuItem data-testid={'column-filtered-selection-' + index} key={'column-filtered-selection-' + index} value={bs.type}>
                                        <div className={buildingConfStyles.selectDetails}>
                                            <div>

                                                <span className={buildingConfStyles.boldLabel}>{bs.name}</span>
                                                {bs.subname &&
                                                    <span className={buildingConfStyles.smallExplanation}>
                                                        &nbsp;en {bs.subname}
                                                    </span>
                                                }
                                            </div>
                                            <div>
                                                {bs.unitDetails.map((s, index) => {
                                                    return <div key={'detail-' + index} className={buildingConfStyles.smallExplanation}>
                                                        {s.unit} unit - {s.details}
                                                    </div>
                                                })}
                                            </div>
                                        </div>
                                    </MenuItem>
                                )
                            })
                        }
                    </Select>
                </FormControl>
                {
                    chosenColType ?
                        <div className={buildingConfStyles.formControl}>
                            {editing ?
                                <Button color="primary" variant="contained" data-testid="archive-site" aria-label="kolom toevoegen" component="span"
                                    onClick={(e) => {
                                        const id = RefineService.handleColumnTypeChange(chosenColType, part, part.columns.find(c => c.id === selectedColumnId), segmentTypesMultipleFloors, updateBuildingColumn, setUpDefaultColumnFromDefinition);
                                        setAddingColumn(false);
                                        setEditingColumn(true);
                                        setChangingColumnType(false);
                                        updateOptions(selectedColumnId);
                                    }
                                    }>
                                    Kolom Aanpassen
                                </Button> :
                                <Button color="primary" variant="contained" data-testid="archive-site" aria-label="kolom toevoegen" component="span"
                                    onClick={(e) => {
                                        const id = RefineService.addColumn(e, part, undefined, alignment.right, chosenColType, updateBuildingColumns, setUpDefaultColumnFromDefinition);
                                        setAddingColumn(false);
                                        setEditingColumn(true);
                                        setChangingColumnType(false);
                                        updateOptions(id);
                                    }
                                    }>
                                    Kolom Toevoegen
                                </Button>
                            }
                        </div>
                        : <></>
                }

            </div>
            : <>Geen kolomen gevonden voor deze filter kombinatie</>
    }

    const getSelectColumnOptions = () => {
        return columnOptions && columnOptions.length &&
            <div className={buildingConfStyles.formControl}>
                <FormControl size="medium" fullWidth >
                    <InputLabel id={"column-selection-label"}>Selecteer Kolom</InputLabel>
                    <Select
                        labelId={"column-selection-label"}
                        id={"column-selection-id"}
                        data-testid="column-selection-id"
                        value={selectedColumnId}
                        label="Selecteer Kolom"
                    >
                        {
                            columnOptions.map((bs, index) => {
                                return (
                                    <MenuItem onClick={() => {
                                        setHighlightingDisabled(true);
                                        // this is done to prevent highlighting when select component still renders closing animation
                                        setTimeout(() => {
                                            setHighlightingDisabled(false);
                                        }, 1000)
                                        ServiceManager.sceneManager.modelLoaderService.highlightGroupsSugroup(buildingId, bs.id, 'select');
                                        setSelectedColumnId(bs.id)
                                    }} onMouseEnter={(evt) => {
                                        if (!highlightingDisabled) {
                                            ServiceManager.sceneManager.modelLoaderService.highlightGroupsSugroup(buildingId, bs.id, 'highlight');
                                        }
                                    }}
                                        onMouseLeave={(evt) => {
                                            ServiceManager.sceneManager.modelLoaderService.highlightGroupsSugroup(buildingId, selectedColumnId, 'select');
                                        }}

                                        data-testid={'column-selection-' + index} key={'column-selection-' + index} value={bs.id}>
                                        <div className={buildingConfStyles.selectDetails}>
                                            <div>

                                                <span className={buildingConfStyles.boldLabel}>{bs.name}</span>
                                                {bs.subname &&
                                                    <span className={buildingConfStyles.smallExplanation}>
                                                        &nbsp;en {bs.subname}
                                                    </span>
                                                }
                                            </div>
                                            <div>
                                                {bs.unitDetails.map((s, index) => {
                                                    return <div key={'detail-' + index} className={buildingConfStyles.smallExplanation}>
                                                        {s.unit} unit - {s.details}
                                                    </div>
                                                })}
                                            </div>
                                        </div>
                                    </MenuItem>
                                )
                            })
                        }
                    </Select>
                </FormControl>
            </div>
    }

    const getSegmentForm = (part: Part<any, any>, column: Column<any, any, any, any>, segment: string) => {
        if (column) {
            const segmentSubtypes = RefineService.getSegmentTypeSubtypes(column.type, segment, columnDefs) as string[];
            const colSegment = column.segments.find(x => x.type === segment) as Segment<any, any, any>;
            const segVariants = RefineService.getSegmentFloorVariants(column.type, colSegment.subtype, segment, !segmentTypesMultipleFloors.includes(segment as string) ? colSegment.floorsInSegment : 1, columnDefs);

            const floorVariants = RefineService.getSegmentFloorDefinitionsForSubtype(column.type, colSegment.subtype, segment, columnDefs);
            return (
                <div key={'segment-type-confi' + part.type + column.type + segment}>
                    {
                        segmentTypesAllowCustom.includes(segment) &&
                        <>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={colSegment.custom ? true : false}
                                            onChange={() => RefineService.handleColumnSegmentCustomChange(part, column, segment, columnDefs, floorHeight, updateBuildingColumn)}
                                        />
                                    }
                                    label="Maatwerk Plint"
                                />
                            </FormGroup>
                            {colSegment.custom &&
                                <>
                                    <div className={styles.formControl}>
                                        <TextField
                                            id={"segment-custom-height" + part.type + column.type + column.order + segment}
                                            label="Hoogte"
                                            fullWidth
                                            type="number"
                                            InputProps={{ inputProps: { min: 0, max: 100 } }}
                                            value={colSegment.customSegmentHeight ? colSegment.customSegmentHeight : 0}
                                            onChange={(e) => { RefineService.handleColumnSegmentWidthAndHeight(part, column, segment, colSegment.customSegmentWidth, +e.target.value, updateBuildingColumn) }}
                                        />
                                    </div>
                                    <div className={styles.formControl}>
                                        <TextField
                                            id={"segment-custom-width" + part.type + column.type + column.order + segment}
                                            label="Diepte"
                                            type="number"
                                            fullWidth
                                            InputProps={{ inputProps: { min: 0, max: 100 } }}
                                            value={colSegment.customSegmentWidth ? colSegment.customSegmentWidth : 0}
                                            onChange={(e) => { RefineService.handleColumnSegmentWidthAndHeight(part, column, segment, +e.target.value, colSegment.customSegmentHeight, updateBuildingColumn) }}
                                        />
                                    </div>
                                </>}
                        </>
                    }
                    {
                        !colSegment.custom &&
                        <>
                            {!segmentTypesMultipleFloors.includes(segment as string) &&
                                <>
                                    {
                                        segmentSubtypes && segmentSubtypes.length > 1 &&
                                        <div className={styles.formControl}>
                                            <FormControl size="small" fullWidth>
                                                <InputLabel id={"segment-subtype-selection-" + part.type + column.type + segment}>Subtype</InputLabel>
                                                <Select
                                                    labelId={"segment-subtype-selection-" + part.type + column.type + segment}
                                                    id={"segment-subtype-selection-select" + part.type + column.type + segment}
                                                    data-testid="segment-subtype-selection-id"
                                                    value={colSegment.subtype ? colSegment.subtype : ''}
                                                    onChange={(event) => RefineService.handleSegmentSubtypeChange(event, part, column, segment, columnDefs, updateBuildingColumn)}
                                                    label="Segment Subtype"
                                                >
                                                    {
                                                        segmentSubtypes.map((subtype, index) => {
                                                            return (
                                                                <MenuItem data-testid={'segment-subtype-select-' + subtype} key={'segment-subtype-menuitem-' + part.type + column.type + subtype + index} value={subtype}>
                                                                    {getSegmentSubtypeName(subtype)}
                                                                </MenuItem>
                                                            )
                                                        })
                                                    }
                                                </Select>
                                            </FormControl>
                                        </div>
                                    }
                                </>
                            }
                            {
                                segmentSubtypes && segmentSubtypes.length === 1 &&
                                <div className={styles.formControl}>
                                    <TextField
                                        id={'segment-subtype-readonly-' + part.type + column.type + segmentSubtypes[0]}
                                        label="Subtype"
                                        fullWidth
                                        InputProps={{
                                            readOnly: true,
                                            disabled: true,
                                        }}
                                        value={segmentSubtypes[0] ? getSegmentSubtypeName(segmentSubtypes[0]) : ''}
                                        onChange={() => { }}
                                    />
                                </div>

                            }
                            {!segmentTypesMultipleFloors.includes(segment as string) &&
                                <>
                                    {floorVariants && floorVariants.length > 1 &&
                                        <div className={styles.formControl}>
                                            <FormControl size="small" fullWidth>
                                                <InputLabel id={"segment-floor-selection-" + part.type + column.type + column.order + segment}>Verdiepingen</InputLabel>
                                                <Select
                                                    labelId={"segment-floor-selection-" + part.type + column.type + column.order + segment}
                                                    id={"segment-floor-selection-id-" + part.type + column.type + column.order + segment}
                                                    data-testid="segment-floor-selection-id"
                                                    value={colSegment.floorsInSegment ? colSegment.floorsInSegment : 0}
                                                    onChange={(event) => RefineService.handleColumnSegmentFloorAndVariantChange(event, part, column, segment, false, segmentTypesMultipleFloors, updateBuildingColumn, columnDefs)}
                                                    label="Segment Floors"
                                                >
                                                    {
                                                        floorVariants.map((f, index) => {
                                                            return (
                                                                <MenuItem data-testid={'segment-floor-select-' + part.type + column.type + f} key={'segment-floor-menuitem-' + part.type + column.type + segment + f.floors + index} value={+f.floors}>
                                                                    {f.floors}
                                                                </MenuItem>
                                                            )
                                                        })
                                                    }
                                                </Select>
                                            </FormControl>
                                        </div>
                                    }
                                    {floorVariants && floorVariants.length === 1 &&
                                        <div className={styles.formControl}>
                                            <TextField
                                                id={"segment-floor-input-id-" + part.type + column.type + column.order + segment}
                                                label="Verdiepingen"
                                                fullWidth
                                                InputProps={{
                                                    readOnly: true,
                                                    disabled: true,
                                                }}
                                                value={floorVariants[0].floors}
                                                onChange={() => { }}
                                            />
                                        </div>
                                    }
                                </>
                            }
                            {segmentTypesMultipleFloors.includes(segment as string) &&
                                <div className={styles.formControl}>
                                    <TextField
                                        id={"segment-floor-input-id-" + part.type + column.type + column.order + segment}
                                        label="Verdiepingen"
                                        fullWidth
                                        type="number"
                                        InputProps={{ inputProps: { min: 0, max: 100 } }}
                                        value={colSegment.floorsInSegment}
                                        onChange={(event) => { RefineService.handleColumnSegmentFloorAndVariantChange(event, part, column, segment, false, segmentTypesMultipleFloors, updateBuildingColumn, columnDefs) }}
                                    />
                                </div>
                            }
                            {
                                segVariants && segVariants.length > 1 &&
                                <div className={`${styles.secondRowFormControl} ${styles.formControl}`}>
                                    <FlatOptions label={'Uitlijning'} activeOption={colSegment.variant ? colSegment.variant : (part.type === 'headRight' && segVariants.length === 2) ? segVariants[1].variant : segVariants[0].variant} setActiveOptionValue={(event) => RefineService.handleColumnSegmentFloorAndVariantChange(event, part, column, segment, true, segmentTypesMultipleFloors, updateBuildingColumn, columnDefs)} options={segVariants.map(sv => ({ title: getVariantName(sv.variant), value: sv.variant }))} />
                                </div>
                            }
                        </>
                    }
                </div>)
        }
    }

    const getActionButtons = () => {
        return <div className={styles.formControl}>
            <Tooltip title="Kolom Toevoegen">
                <IconButton size="small" onClick={() => { setAddingColumn(!addingColumn); setChangingColumnType(false); setEditingColumn(addingColumn) }} color="primary" data-testid="edit" aria-label="Toevoegen" component="span">
                    <Add />
                </IconButton>
            </Tooltip>
            <Tooltip title="Kolom Links Verplaatsen">
                <IconButton size="small" onClick={() => {
                    RefineService.moveColumnLeft(selectedColumnId, part, false, updateBuildingColumns, updateOptions);
                }} color="primary" data-testid="edit" aria-label="Links" component="span">
                    <ArrowLeft />
                </IconButton>
            </Tooltip>
            <Tooltip title="Kolom Rechts Verplaatsen">
                <IconButton size="small" onClick={() => {
                    RefineService.moveColumnRight(selectedColumnId, part, true, updateBuildingColumns, updateOptions)
                }} color="primary" data-testid="edit" aria-label="Rechts" component="span">
                    <ArrowRight />
                </IconButton>
            </Tooltip>
            <Tooltip title="Kolom Wijzigen">
                <IconButton size="small" onClick={() => { setChangingColumnType(!changingColumnType); setEditingColumn(changingColumnType); setAddingColumn(false); }} color="primary" data-testid="edit" aria-label="Wijzigen" component="span">
                    <Edit />
                </IconButton>
            </Tooltip>
            <Tooltip title="Kolom Dupliceren">
                <IconButton size="small" onClick={(e) => RefineService.addColumn(e, part, part.columns.find(s => s.id === selectedColumnId), alignment.right, getDefaultColumnTypeForPart(part), updateBuildingColumns, setUpDefaultColumnFromDefinition)} color="primary" data-testid="edit" aria-label="Kolom Dupliceren" component="span">
                    <ContentCopy />
                </IconButton>
            </Tooltip>
            <Tooltip title="Kolom Verwijderen">
                <IconButton size="small" onClick={(e) => {
                    RefineService.deleteColumn(e, part, part.columns.find(s => s.id === selectedColumnId), updateBuildingColumns)
                }
                } color="primary" data-testid="edit" aria-label="Verwijderen" component="span">
                    <Delete />
                </IconButton>
            </Tooltip>
        </div>
    }

    return (
        <div data-testid="Refine">
            {
                part.columns.length ?
                    getSelectColumnOptions() : <></>
            }
            {
                part.columns.length ?
                    getActionButtons() : <></>
            }
            {
                part.columns.length === 0 &&
                <Tooltip title="Eerste Kolom Toevoegen">
                    <Button color="primary" variant="outlined" data-testid="archive-site" aria-label="eerste kolom toevoegen" component="span" onClick={(e) => RefineService.addColumn(e, part, undefined, alignment.right, getDefaultColumnTypeForPart(part), updateBuildingColumns, setUpDefaultColumnFromDefinition)}>
                        Kolom Toevoegen
                    </Button>
                </Tooltip>
            }
            {
                addingColumn ?
                    <>
                        {
                            bt !== buildingType.rowhouse ?
                                <ToggleOptions label={'Filter'} options={filterOptions} setOptions={setFilterOptions} /> : <></>
                        }
                        {
                            getFilteredColumnOptions(false)
                        }
                    </>
                    : <></>
            }
            {
                changingColumnType ?
                    <>
                        {
                            bt !== buildingType.rowhouse ?
                                <ToggleOptions label={'Filter'} options={filterOptions} setOptions={setFilterOptions} /> : <></>
                        }
                        {
                            getFilteredColumnOptions(true)
                        }
                    </>
                    : <></>
            }
            {
                part.columns.length &&
                    editingColumn ?
                    <>
                        {
                            SegmentComponent && <SegmentComponent activeSegment={activeSegment as any} setActiveSegment={setActiveSegment} getSegmentName={getSegmentName} />
                        }
                        {
                            getSegmentForm(part, part.columns.find(c => c.id === selectedColumnId), activeSegment)
                        }
                    </> : <></>
            }
        </div>
    )
};

export default Refine;
